import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from "react-i18next";
import '../../hs-theme.css';
import './guide.css';

const SECTIONS = ['s1', 's2', 's3', 's4', 's5'];
const CONDITIONS = ['cond_1', 'cond_2', 'cond_3', 'cond_4', 'cond_5'];
// [who, recognized area in m²]
const HOUSEHOLD = [['member_buyer', 40], ['member_spouse', 15], ['member_child_1', 15], ['member_child_2', 15]];
// [text, tags]; a tag is shown as written unless it is a key of the texts
const STEPS = [
    ['step_1', ['RFN']],
    ['step_2', ['IKPS-PDV']],
    ['step_3', []],
    ['step_4', []],
    ['step_5', ['tag_decision', 'tag_refund']],
];
const WAYS = [
    ['way_person', "M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"],
    ['way_mail', "M3 6h18v12H3ZM3 7l9 6 9-6"],
    ['way_online', "M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm0 0v1.5a2.5 2.5 0 0 0 5 0V12a9 9 0 1 0-3.5 7.1"],
];
// the example: 60 m² at 2.000 € per m², VAT of 10% included
const CALCULATION = [
    ['calc_price', '120.000 €', '60 m² × 2.000 €'],
    ['calc_vat', '10.909,09 €', '120.000 € × 10 / 110'],
    ['calc_refund', '7.272,73 €', '10.909,09 € × 40 / 60'],
];
const PARAGRAF_URL = 'https://www.paragraf.rs';
const TAX_URL = 'https://www.purs.gov.rs';
// a section counts as reached when its top is this close to the top of the window
const ACTIVE_OFFSET = 160;

const Icon = ({ path, size = 22, width = 1.9 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg>
);

const Hexagon = (props) => (
    <svg viewBox="0 0 40 44" fill="none" {...props}><path d="M20 1.5 38 11.75v20.5L20 42.5 2 32.25v-20.5Z" fill="#fff" /></svg>
);

const Source = ({ href, children }) => (
    <a className='hs-article-source' href={href} target='_blank' rel='noopener noreferrer'>{children} ↗</a>
);

const SectionHead = ({ number, children }) => (
    <div className='hs-article-head'>
        <span className='hs-article-number'>{number}</span>
        <h2>{children}</h2>
    </div>
);

export const TaxRefundGuide = () => {
    const { t } = useTranslation(["guide"]);
    const [active, setActive] = useState(SECTIONS[0]);

    // the contents list marks the section being read
    useEffect(() => {
        const onScroll = () => {
            let current = SECTIONS[0];
            SECTIONS.forEach(id => {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top < ACTIVE_OFFSET) current = id;
            });
            setActive(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const jump = (id) => (e) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };
    const rich = (key) => <Trans t={t} i18nKey={key} components={{ b: <strong /> }} />;
    const total = HOUSEHOLD.reduce((sum, [, area]) => sum + area, 0);

    return (
        <div className='hs hs-article'>
            <section className='hs-article-intro'>
                <div className='hs-article-intro-inner'>
                    <nav className='hs-article-crumbs'>
                        <Link to='/'>{t("crumb_home")}</Link><span>/</span>
                        <Link to='/uputstvo'>{t("crumb_guide")}</Link><span>/</span>
                        <span className='current'>{t("crumb_buying")}</span>
                    </nav>
                    <div className='hs-article-tags'>
                        <span className='blue'>{t("tag_buyers")}</span>
                        <span>{t("tag_reading")}</span>
                    </div>
                    <h1>{t("title")}</h1>
                    <p className='hs-article-lead'>{t("lead")}</p>
                    <p className='hs-article-regimes-label'>{t("regimes")}</p>
                    <div className='hs-article-regimes'>
                        <a href='#s1' className='hs-article-regime primary' onClick={jump('s1')}>
                            <span className='hs-article-regime-icon'><Icon path="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6" /></span>
                            <span className='hs-article-regime-text'>
                                <span className='hs-article-regime-title'>{t("regime_new_title")}</span>
                                <span>{t("regime_new_text")}</span>
                            </span>
                        </a>
                        <a href='#s5' className='hs-article-regime' onClick={jump('s5')}>
                            <span className='hs-article-regime-icon'><Icon path="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 10h4a1 1 0 0 1 1 1v10M3 21h18M8 8h3M8 12h3M8 16h3" /></span>
                            <span className='hs-article-regime-text'>
                                <span className='hs-article-regime-title'>{t("regime_old_title")}</span>
                                <span>{t("regime_old_text")}</span>
                            </span>
                        </a>
                    </div>
                </div>
            </section>

            <div className='hs-article-body'>
                <aside className='hs-article-toc'>
                    <p>{t("toc")}</p>
                    {SECTIONS.map((id, i) => (
                        <a key={id} href={`#${id}`} className={id === active ? 'on' : ''} onClick={jump(id)}>
                            <span>{i + 1}</span>{t(`${id}_title`)}
                        </a>
                    ))}
                </aside>

                <article className='hs-article-article'>
                    <section id='s1' className='hs-article-section'>
                        <SectionHead number={1}>{t("s1_title")}</SectionHead>
                        <p className='hs-article-text'>{t("s1_intro")}</p>
                        <div className='hs-article-card hs-article-conditions'>
                            {CONDITIONS.map(key => (
                                <div key={key}>
                                    <span className='hs-article-check'><Icon path="m5 12.5 4.5 4.5L19 7.5" size={13} width={3.2} /></span>
                                    <span>{t(key)}</span>
                                </div>
                            ))}
                        </div>
                        <p className='hs-article-note'>{t("s1_note")}<Source href={PARAGRAF_URL}>{t("source_paragraf")}</Source></p>
                    </section>

                    <section id='s2' className='hs-article-section'>
                        <SectionHead number={2}>{t("s2_title")}</SectionHead>
                        <div className='hs-article-areas'>
                            <div className='hs-article-card hs-article-area'>
                                <span className='hs-article-area-label'>{t("up_to")}</span>
                                <span className='hs-article-area-value'>40 m²</span>
                                <span className='hs-article-area-who'>{t("for_buyer")}</span>
                                <span className='hs-article-area-text'>{t("buyer_area_text")}</span>
                            </div>
                            <div className='hs-article-card hs-article-area'>
                                <span className='hs-article-area-label'>{t("additional")}</span>
                                <span className='hs-article-area-value'>+15 m²</span>
                                <span className='hs-article-area-who'>{t("per_member")}</span>
                                <span className='hs-article-area-text'>{t("member_area_text")}</span>
                            </div>
                        </div>
                        <p className='hs-article-text'>{t("household_intro")}</p>
                        <div className='hs-article-card hs-article-table'>
                            <div className='hs-article-table-head'><span>{t("who")}</span><span>{t("area")}</span></div>
                            {HOUSEHOLD.map(([who, area], i) => (
                                <div key={who} className={`hs-article-table-row ${i === 0 ? 'buyer' : ''}`}>
                                    <span className='hs-article-member'>
                                        <span className='hs-article-member-icon'><Icon path="M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" size={16} width={2} /></span>
                                        {t(who)}
                                    </span>
                                    <span className='hs-article-member-area'>
                                        <span className='hs-article-bar' style={{ width: Math.round(area * 2.2) }}></span>
                                        <span className='hs-article-member-value'>{area} m²</span>
                                    </span>
                                </div>
                            ))}
                            <div className='hs-article-table-total'><span>{t("total")}</span><span>{total} m²</span></div>
                        </div>
                        <div className='hs-article-important'>
                            <span className='hs-article-important-icon'><Icon path="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 8v5M12 16h.01" size={17} width={2.2} /></span>
                            <p>{rich("important")}</p>
                        </div>
                        <Source href={PARAGRAF_URL}>{t("source_paragraf")}</Source>
                    </section>

                    <section id='s3' className='hs-article-section'>
                        <SectionHead number={3}>{t("s3_title")}</SectionHead>
                        <p className='hs-article-text'>{rich("example_intro")}</p>
                        <div className='hs-article-calc'>
                            <Hexagon className='hs-article-calc-mark' width={260} height={286} />
                            {CALCULATION.map(([label, value, formula], i) => (
                                <div key={label} className={i === CALCULATION.length - 1 ? 'result' : ''}>
                                    <span className='hs-article-calc-label'>{t(label)}</span>
                                    <span className='hs-article-calc-value'>{value}</span>
                                    <span className='hs-article-calc-formula'>{formula}</span>
                                </div>
                            ))}
                        </div>
                        <p className='hs-article-note'>{t("example_note")}</p>
                    </section>

                    <section id='s4' className='hs-article-section'>
                        <SectionHead number={4}>{t("s4_title")}</SectionHead>
                        <p className='hs-article-text'>{t("s4_intro")}</p>
                        <div className='hs-article-ways'>
                            {WAYS.map(([key, path]) => (
                                <span key={key} className='hs-article-way'><Icon path={path} size={16} width={2} />{t(key)}</span>
                            ))}
                            <Source href={TAX_URL}>{t("source_tax")}</Source>
                        </div>
                        <div className='hs-article-steps'>
                            {STEPS.map(([text, tags], i) => (
                                <div key={text} className={`hs-article-step ${i === STEPS.length - 1 ? 'last' : ''}`}>
                                    <div className='hs-article-step-rail'>
                                        <span className='hs-article-step-number'>{i + 1}</span>
                                        <span className='hs-article-step-line'></span>
                                    </div>
                                    <div className='hs-article-card hs-article-step-card'>
                                        <span>{t(text)}</span>
                                        {tags.length > 0 &&
                                            <div className='hs-article-step-tags'>
                                                {tags.map(tag => <span key={tag}>{t(tag, tag)}</span>)}
                                            </div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <a className='hs-article-card hs-article-official' href={TAX_URL} target='_blank' rel='noopener noreferrer'>
                            <span className='hs-article-official-icon'><Icon path="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8ZM14 3v5h5M9 13h6M9 17h4" /></span>
                            <span className='hs-article-official-text'>
                                <span>{t("official_label")}</span>
                                <span className='hs-article-official-title'>{t("official_title")}</span>
                            </span>
                            <span className='hs-article-official-go'>↗</span>
                        </a>
                    </section>

                    <section id='s5' className='hs-article-section'>
                        <SectionHead number={5}>{t("s5_title")}</SectionHead>
                        <div className='hs-article-card hs-article-old'>
                            <p className='hs-article-text'>{rich("s5_text")}</p>
                            <div className='hs-article-old-areas'>
                                <div><span>40 m²</span><span>{t("for_buyer")}</span></div>
                                <div><span>+15 m²</span><span>{t("per_member")}</span></div>
                            </div>
                        </div>
                    </section>

                    <section className='hs-article-cta'>
                        <Hexagon className='hs-article-cta-mark' width={240} height={264} />
                        <span className='hs-article-cta-title'>{t("cta_title")}</span>
                        <Link to='/prodaja' onClick={() => sessionStorage.removeItem('currentPage')}>{t("cta_button")} →</Link>
                    </section>

                    <p className='hs-article-disclaimer'>{t("disclaimer")}</p>
                </article>
            </div>
        </div>
    );
};
